/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-09-05 10:34:47
 */
import React, { useEffect, useState } from 'react'
import { Button, Layout } from 'antd'
import AntdProvider from '@/components/AntdProvider'

import {
  AIRecognitionProvider,
  useAIRecognition
} from './context/AIRecognitionContext'
import {
  SelectImageArea,
  RecognitionContentRecognizing,
  RecognitionContentCompleted,
  ExportResultButton,
  RecognitionContentWaiting,
  ExamplePhotos
} from './components'
import { useSensorTracking, useSafetyScenarioSelection } from './hooks'
import FunctionalFeedback from '../components/FunctionalFeedback'
import FeedbackModal from './components/FeedbackModal'
import GenericSelectorModal from '../components/GenericSelectorModal'
import { XC_RECOGNITION_STATUS, XCSafetyScenario } from './types'
import './index.scss'
import verify_bg from '../../images/verify-auth/verify_bg.jpg'

import PageHeader from '@/components/PageHeader'
// 默认安全场景数据
const DEFAULT_SCENARIOS: XCSafetyScenario[] = [
  {
    id: '1',
    name: '高空作业',
    description: '高空作业安全隐患',
    selected: false
  }
]
const { Content } = Layout

const AIRecognitionContent: React.FC = React.memo(() => {
  const {
    recognitionStatus,
    safetyScenarioList,
    rememberChoice
  } = useAIRecognition()

  // 反馈Modal状态
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false)

  // 使用安全场景选择hook
  const {
    safetyScenarios,
    showSafetyScenarioModal,
    showSafetyScenarioSelection,
    handleSafetyScenarioConfirm,
    handleSafetyScenarioCancel
  } = useSafetyScenarioSelection()

  const {
    reportAISafetyHazardsClick,
    reportAISafetyHazardsFeedback
  } = useSensorTracking()

  // 页面进入埋点
  useEffect(() => {
    reportAISafetyHazardsClick({ clickItem: 'enterPage' })
  }, [reportAISafetyHazardsClick])

  /**
   * 处理识别记录按钮点击
   */
  const handleRecognitionHistory = () => {
    // 上报识别记录点击埋点
    reportAISafetyHazardsClick({ clickItem: 'identifyHistory' })
    // 打开识别记录页面
    window.open('/ai/recognition/export-list?fromPlace=aiRecognition')
  }

  return (
    <div className="ai-recognition-page">
      {/* 页面头部 */}
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)',
          overflow: 'visible'
        }}
        title={
          <span
            style={{
              background: 'linear-gradient(90deg, #1C4AF7 0%, #0FBCF8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 'bold'
            }}
          >
            AI安全隐患识别
          </span>
        }
        extra={[
          <Button
            key="history"
            onClick={handleRecognitionHistory}
            style={{
              backgroundColor: '#EBF6FF',
              border: 'none',
              color: '#0070F4',
              fontWeight: 'bold'
            }}
          >
            识别记录
          </Button>
        ]}
      />

      <Content
        style={{
          backgroundImage: `url(${verify_bg})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          minHeight: '100%'
        }}
      >
        {/* 1440px 宽度容器，居中显示 */}
        <div
          style={{
            maxWidth: '1440px',
            width: '100%',
            margin: '0 auto'
          }}
        >
          {/* 主要内容区域 - 水平布局 */}
          <div
            style={{
              display: 'flex'
              // gap: '24px'
            }}
          >
            {/* 左侧图片上传区域 - 固定宽度 */}
            <div
              style={{
                width: 'auto',
                flexShrink: 0,
                alignItems: 'center',
                marginTop: 40,
                marginLeft: 40
              }}
            >
              <SelectImageArea
                onShowSafetyScenarioSelection={showSafetyScenarioSelection}
              />
            </div>

            {/* 右侧识别内容区域 - 自适应宽度 */}
            <div
              style={{
                flex: 1,
                marginLeft: 24,
                marginRight: 24,
                marginTop: 40
              }}
            >
              {/* 根据识别状态渲染对应组件 */}
              {(() => {
                switch (recognitionStatus) {
                  case XC_RECOGNITION_STATUS.WAITING:
                    return (
                      // <RecognitionContentWaitingV2
                      //   onShowSafetyScenarioSelection={
                      //     showSafetyScenarioSelection
                      //   }
                      // />
                      <RecognitionContentWaiting />
                    )
                  case XC_RECOGNITION_STATUS.RECOGNIZING:
                    return <RecognitionContentRecognizing />
                  case XC_RECOGNITION_STATUS.COMPLETED:
                    return (
                      <RecognitionContentCompleted
                        onShowSafetyScenarioSelection={
                          showSafetyScenarioSelection
                        }
                      />
                    )
                  default:
                    return (
                      // <RecognitionContentWaitingV2
                      //   onShowSafetyScenarioSelection={
                      //     showSafetyScenarioSelection
                      //   }
                      // />
                      <RecognitionContentWaiting />
                    )
                }
              })()}

              {/* 操作按钮区域 - 仅在识别完成时显示 */}
              {recognitionStatus === XC_RECOGNITION_STATUS.COMPLETED && (
                <div className="action-buttons-container">
                  {/* 评价结果区域 */}
                  <FunctionalFeedback
                    title="评价结果："
                    options={[
                      {
                        text: '准确-满足工作要求',
                        iconType: 'like',
                        type: 'positive',
                        onClick: () => {
                          reportAISafetyHazardsClick({
                            clickItem: 'feedbackPositive',
                            bool1: rememberChoice
                          })
                          setFeedbackModalVisible(true)
                        }
                      },
                      {
                        text: '不准-反馈优化建议',
                        iconType: 'dislike',
                        type: 'negative',
                        onClick: () => {
                          reportAISafetyHazardsClick({
                            clickItem: 'feedback',
                            bool1: rememberChoice
                          })
                          setFeedbackModalVisible(true)
                        }
                      }
                    ]}
                  />

                  {/* 导出分析结果按钮 */}
                  <ExportResultButton />
                </div>
              )}
            </div>
          </div>

          {/* 示例照片区域 */}
          <div style={{ paddingTop: '32px', paddingBottom: '40px' }}>
            <ExamplePhotos />
          </div>
        </div>
      </Content>

      {/* 反馈Modal */}
      <FeedbackModal
        visible={feedbackModalVisible}
        onCancel={() => setFeedbackModalVisible(false)}
        onSubmit={(
          score: number,
          content: string,
          willPhoneCallback?: boolean
        ) => {
          // 上报满意度反馈埋点
          reportAISafetyHazardsFeedback({
            score: score,
            content: content,
            willPhoneCallback: willPhoneCallback
          })
          setFeedbackModalVisible(false)
        }}
      />

      {/* 安全场景选择Modal */}
      <GenericSelectorModal<XCSafetyScenario>
        visible={showSafetyScenarioModal}
        title="选择安全隐患大模型"
        closable={false}
        selectionType="single"
        maskClosable={false}
        columnsPerRow={3}
        dataSource={safetyScenarioList ?? DEFAULT_SCENARIOS}
        onConfirm={handleSafetyScenarioConfirm}
        onCancel={handleSafetyScenarioCancel}
        confirmText="选好了"
        width={1000}
        enableCustomInput={true}
        customOptionText="自定义"
        customInputPlaceholder="请输入自定义内容"
        enableRememberChoice={true}
        rememberChoiceText="记住我的选择"
        onRememberChoiceChange={(selectedItems, rememberChoice) => {
          reportAISafetyHazardsClick({
            clickItem: 'rememberChoice',
            bool1: rememberChoice,
            string1: JSON.stringify(selectedItems)
          })
        }}
      />
    </div>
  )
})

/**
 * AI识别页面主组件
 */
const AIRecognition: React.FC = React.memo(() => {
  return (
    <AntdProvider>
      <AIRecognitionProvider>
        <AIRecognitionContent />
      </AIRecognitionProvider>
    </AntdProvider>
  )
})

export default AIRecognition
